import { useEffect, useMemo, useState } from 'react';
import { ScanLine } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NativeSelect } from '@/components/ui/native-select';
import { SearchableSelect } from './SearchableSelect';
import { TrackingSourceEnum, type InboundTrackingEntry } from '@/apis/warehouse';
import { useAddInboundTracking, useUpdateInboundTracking, useWarehouseHandlers } from '@/hooks/useWarehouse';
import { useAuthStore } from '@/stores/authStore';
import { CARRIER_OPTIONS } from './warehouse-shared';
import { playBell, playBuzzer } from '@/lib/sound';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  assignedSalespersonId?: string | null;
  assignedWarehouseHandlerId?: string | null;
  /** When provided, the dialog edits this existing record instead of creating a new one. */
  entry?: InboundTrackingEntry;
}

/** Resolves the acting user's side of the comparison the same way the backend does — locked for sales/handler, chosen explicitly for admin. */
function useResolvedSource(assignedSalespersonId?: string | null, assignedWarehouseHandlerId?: string | null) {
  const user = useAuthStore((s) => s.user);
  if (!user) return { locked: null, isAdmin: false };
  if (user.id === assignedSalespersonId) return { locked: TrackingSourceEnum.SALES, isAdmin: false };
  if (user.id === assignedWarehouseHandlerId) return { locked: TrackingSourceEnum.WAREHOUSE, isAdmin: false };
  if (user.userRole === 'admin') return { locked: null, isAdmin: true };
  if (!assignedSalespersonId && user.isSalesperson) return { locked: TrackingSourceEnum.SALES, isAdmin: false };
  if (!assignedWarehouseHandlerId && user.isWarehouseHandler) return { locked: TrackingSourceEnum.WAREHOUSE, isAdmin: false };
  return { locked: TrackingSourceEnum.WAREHOUSE, isAdmin: false };
}

export function AddInboundTrackingDialog({
  open,
  onOpenChange,
  orderId,
  assignedSalespersonId,
  assignedWarehouseHandlerId,
  entry,
}: Props) {
  const isEditing = !!entry;
  const { locked: lockedSource, isAdmin } = useResolvedSource(assignedSalespersonId, assignedWarehouseHandlerId);

  const [source, setSource] = useState<TrackingSourceEnum | null>(lockedSource);
  const [carrier, setCarrier] = useState<string>(CARRIER_OPTIONS[0]);
  const [customCarrier, setCustomCarrier] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [managerId, setManagerId] = useState('');
  const [note, setNote] = useState('');

  const { data: handlers = [] } = useWarehouseHandlers();
  const addInbound = useAddInboundTracking(orderId);
  const updateInbound = useUpdateInboundTracking(orderId);
  const isPending = addInbound.isPending || updateInbound.isPending;

  useEffect(() => {
    if (!open) return;
    setSource(isEditing ? entry.source : lockedSource);
    const existingCarrier = entry?.courierOrSupplierName ?? '';
    const isKnownCarrier = (CARRIER_OPTIONS as readonly string[]).includes(existingCarrier);
    setCarrier(existingCarrier && isKnownCarrier ? existingCarrier : existingCarrier ? 'Other' : CARRIER_OPTIONS[0]);
    setCustomCarrier(existingCarrier && !isKnownCarrier ? existingCarrier : '');
    setTrackingNumber(entry?.trackingNumber ?? '');
    setManagerId(entry?.attributedToId ?? '');
    setNote(entry?.note ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry]);

  const resolvedCarrierName = useMemo(
    () => (carrier === 'Other' ? customCarrier.trim() : carrier),
    [carrier, customCarrier]
  );

  const canSave =
    !!resolvedCarrierName && !!trackingNumber.trim() && (isEditing || !!source);

  const handleSave = () => {
    if (!canSave) return;
    const data = {
      courierOrSupplierName: resolvedCarrierName,
      trackingNumber: trackingNumber.trim(),
      note: note.trim() || undefined,
      attributedToId: managerId || undefined,
    };
    if (isEditing) {
      updateInbound.mutate({ trackingId: entry.id, ...data }, { onSuccess: () => onOpenChange(false) });
      return;
    }
    addInbound.mutate(
      { ...data, source: source ?? undefined },
      {
        onSuccess: (result) => {
          if (result.source === TrackingSourceEnum.WAREHOUSE) {
            if (result.isMatch) playBell();
            else playBuzzer();
          }
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-110">
        <DialogHeader className="border-b border-smoke pb-3">
          <DialogTitle>Inbound Shipment</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-3">
          {isAdmin && !isEditing && (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setSource(TrackingSourceEnum.SALES)}
                className={`flex-1 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${source === TrackingSourceEnum.SALES ? 'border-primary-s500 bg-primary-s50 text-primary-s500' : 'border-smoke text-lightGray hover:bg-smoke1'}`}
              >
                Sales Input
              </button>
              <button
                type="button"
                onClick={() => setSource(TrackingSourceEnum.WAREHOUSE)}
                className={`flex-1 rounded-xl border px-4 py-2 text-sm font-medium transition-colors ${source === TrackingSourceEnum.WAREHOUSE ? 'border-primary-s500 bg-primary-s50 text-primary-s500' : 'border-smoke text-lightGray hover:bg-smoke1'}`}
              >
                Warehouse Scan
              </button>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Carrier</label>
            <NativeSelect value={carrier} onChange={setCarrier} options={[...CARRIER_OPTIONS]} />
            {carrier === 'Other' && (
              <input
                value={customCarrier}
                onChange={(e) => setCustomCarrier(e.target.value)}
                placeholder="e.g. Costex"
                className="mt-1 h-10 rounded-xl border border-smoke px-3 text-base outline-none focus:border-primary-s500"
              />
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">
              Tracking or PO Number <span className="text-danger">*</span>
            </label>
            <div className="flex gap-2">
              <input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter"
                className="h-10 flex-1 rounded-xl border border-smoke px-3 text-base outline-none focus:border-primary-s500"
              />
              <button
                type="button"
                disabled
                title="Barcode scanning coming soon"
                className="flex h-10 items-center gap-1.5 rounded-xl border border-dashed border-smoke px-3 text-sm text-lightGray"
              >
                <ScanLine size={14} />
                Scan
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Manager</label>
            <SearchableSelect
              value={managerId}
              onValueChange={setManagerId}
              options={handlers.map((h) => ({ value: h.id, label: `${h.firstName} ${h.lastName}` }))}
              placeholder="Select"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Type your notes..."
              className="resize-none rounded-xl border border-smoke px-3 py-2 text-base outline-none focus:border-primary-s500"
            />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-smoke px-5 py-2 text-sm text-black hover:bg-smoke1"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isPending || !canSave}
            className="rounded-full bg-primary-s500 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
